import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { createHousekeeper, updateHousekeeper, deleteHousekeeper } from '@/lib/api';

const emptyHkForm = { name: '', contact: '', availability: '', preference: '', pay: '', notes: '', is_archived: false };

function HousekeeperCard({ housekeeper: h, idx, isArchived, onEdit, onArchive, onDelete }) {
  return (
    <div className={`group border rounded-xl px-4 py-3.5 ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'} ${isArchived ? 'opacity-60' : ''} hover:shadow-sm transition-shadow`} data-testid="housekeeper-row">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{h.name}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
            {h.contact && <span className="text-xs text-muted-foreground">{h.contact}</span>}
            {h.availability && <span className="text-xs text-muted-foreground">Avail: {h.availability}</span>}
            {h.pay && <span className="text-xs text-muted-foreground">Pay: {h.pay}</span>}
            {h.preference && <span className="text-xs text-muted-foreground">Pref: {h.preference}</span>}
          </div>
          {h.notes && <p className="text-xs text-muted-foreground mt-1">{h.notes}</p>}
        </div>
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onArchive}><Archive className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={onDelete}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

export function HousekeepersTab({ housekeepers, showArchived, setShowArchived, onRefresh }) {
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyHkForm });
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm({ ...emptyHkForm }); setDialog(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({
      name: h.name, contact: h.contact || '', availability: h.availability || '',
      preference: h.preference || '', pay: h.pay || '', notes: h.notes || '',
      is_archived: h.is_archived || false
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) { await updateHousekeeper(editing.id, form); toast.success('Updated'); }
      else { await createHousekeeper(form); toast.success('Created'); }
      setDialog(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (h) => {
    try {
      await updateHousekeeper(h.id, { ...h, is_archived: !h.is_archived });
      toast.success(h.is_archived ? 'Restored' : 'Archived');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete permanently?')) return;
    try { await deleteHousekeeper(id); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  const active = housekeepers.filter(h => !h.is_archived);
  const archived = housekeepers.filter(h => h.is_archived);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant={showArchived ? 'secondary' : 'outline'} className="text-xs h-8" onClick={() => setShowArchived(!showArchived)}>
          <Archive className="h-3 w-3 mr-1" />{showArchived ? 'Hide' : 'Show'} Archived
        </Button>
        <Button onClick={openCreate} data-testid="hk-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add Housekeeper
        </Button>
      </div>

      {active.length === 0 && !showArchived && (
        <div className="text-center py-12 text-muted-foreground text-sm border rounded-xl bg-muted/20">No housekeepers added yet</div>
      )}

      <div className="space-y-2" data-testid="housekeepers-list">
        {active.map((h, idx) => (
          <HousekeeperCard key={h.id} housekeeper={h} idx={idx} onEdit={() => openEdit(h)} onArchive={() => handleArchive(h)} onDelete={() => handleDelete(h.id)} />
        ))}
        {showArchived && archived.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2 pb-1 px-1">Archived</p>
            {archived.map((h, idx) => (
              <HousekeeperCard key={h.id} housekeeper={h} idx={idx} isArchived onEdit={() => openEdit(h)} onArchive={() => handleArchive(h)} onDelete={() => handleDelete(h.id)} />
            ))}
          </>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Housekeeper' : 'Add Housekeeper'}</DialogTitle>
            <DialogDescription>Enter housekeeper details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="hk-name-input" /></div>
              <div className="space-y-1"><Label>Contact</Label><Input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Availability</Label><Input value={form.availability} onChange={e => setForm({ ...form, availability: e.target.value })} placeholder="e.g. Mon-Fri" /></div>
              <div className="space-y-1"><Label>Preference</Label><Input value={form.preference} onChange={e => setForm({ ...form, preference: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Pay</Label><Input value={form.pay} onChange={e => setForm({ ...form, pay: e.target.value })} placeholder="e.g. $100/cleaning" /></div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="hk-save-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
